import {useId} from "react";
import {css} from "@panda/css";

import {Checkbox} from "@pg-design/checkbox-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";
import {MULTILEAD_RESPONSIVE_MODAL_TEST_IDS} from "@pg-mono/test-ids";

import {getPropertyBoxMultileadText} from "../../../atoms/get_property_box_multilead_text";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";

const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);

interface IProps {
    property: IRecommendedProperty;
    isSelected: boolean;
    hidePrices: boolean;
    isViewedOffer?: boolean;
    recommendationAdvantages?: {priceDifference: number; areaDifference: number};
    onToggle: () => void;
    onHover: (propertyId: number | null) => void;
}

export const ApplicationMultiLeadResponsiveModalPropertyCard = (props: IProps) => {
    const {property, isSelected, hidePrices, isViewedOffer, recommendationAdvantages, onToggle, onHover} = props;
    const titleId = useId();
    const addressId = useId();
    const comparisonText = isViewedOffer
        ? null
        : getPropertyBoxMultileadText(recommendationAdvantages?.areaDifference || 0, recommendationAdvantages?.priceDifference || 0);
    const badgeText = isViewedOffer ? "Oglądana oferta" : comparisonText;

    return (
        <li
            className={isSelected ? selectedCardStyle : cardStyle}
            onClick={onToggle}
            onMouseEnter={() => onHover(property.id)}
            onMouseLeave={() => onHover(null)}
            data-track-id={property.id}
            data-testid={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.PROPERTY_CARD}
        >
            <div className={isSelected ? selectedCardBoxStyle : cardBoxStyle}>
                <Image
                    src={getThumbnailLink(property.offer?.main_image_url, 350, 230)}
                    alt=""
                    width="99px"
                    height="119px"
                    className={imageWrapperStyle}
                    imageClassName={imageStyle}
                    placeholderIconClasName={placeholderIconStyle}
                />

                <div className={cardContentStyle}>
                    <div className={cardTextStyle}>
                        <Text as="p" id={titleId} variant="info_txt_1" color="var(--colors-secondary)">
                            {`${property.rooms} ${roomsPluralize(property.rooms)}, ${areaFormat(property.area)}`}
                        </Text>
                        <Text as="p" id={addressId} variant="info_txt_3" color="var(--colors-gray-700)" className={addressStyle}>
                            {property.offer.name}, {property.offer.address}
                        </Text>
                    </div>

                    {property.price && !hidePrices ? (
                        <Text as="p" variant="body_copy_1" strong color="var(--colors-secondary)">
                            <Highlight>{priceFormat(property.price)}</Highlight>
                        </Text>
                    ) : null}

                    {badgeText ? (
                        <div className={badgeStyle}>
                            <Text as="span" variant="info_txt_2" color="var(--colors-secondary)">
                                {badgeText}
                            </Text>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className={isSelected ? selectedCheckboxCircleStyle : checkboxCircleStyle}>
                <Checkbox
                    name={`property_${property.id}`}
                    checked={isSelected}
                    value={property.id}
                    onChange={onToggle}
                    aria-labelledby={`${titleId} ${addressId}`}
                    dataTestId={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.PROPERTY_CHECKBOX}
                />
            </div>
        </li>
    );
};

const cardStyle = css({
    position: "relative",
    cursor: "pointer"
});

const selectedCardStyle = css({
    position: "relative",
    cursor: "pointer",
    filter: "drop-shadow(0 4px 2px rgba(0, 0, 0, 0.25))"
});

const cardBoxStyle = css({
    display: "flex",
    columnGap: 1,
    overflow: "hidden",
    borderRadius: 1,
    border: "1px solid var(--colors-gray-300)",
    backgroundColor: "var(--colors-white)"
});

const selectedCardBoxStyle = css({
    display: "flex",
    columnGap: 1,
    overflow: "hidden",
    borderRadius: 1,
    border: "1px solid var(--colors-secondary)",
    backgroundColor: "var(--colors-background-100)"
});

const imageWrapperStyle = css({
    display: "block",
    flex: "0 0 9.9rem",
    alignSelf: "stretch",
    width: "9.9rem",
    height: "auto",
    minHeight: "11.9rem",
    backgroundColor: "var(--colors-gray-300)"
});

const imageStyle = css({
    position: "absolute",
    top: 0,
    left: 0,
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const placeholderIconStyle = css({
    transform: "translate(-50%, -50%) scale(8)",
    opacity: 0.5
});

const cardContentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0,
    py: 1,
    pr: "3.4rem"
});

const cardTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 0.5
});

const addressStyle = css({
    fontSize: "0.842rem",
    lineClamp: 2,
    md: {
        fontSize: "0.983rem"
    }
});

const badgeStyle = css({
    display: "inline-flex",
    alignItems: "center",
    minHeight: "2.4rem",
    px: 1,
    borderRadius: 1,
    backgroundColor: "var(--colors-beige-subtle)"
});

const checkboxCircleStyle = css({
    position: "absolute",
    top: "50%",
    right: "-2.65rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "5.3rem",
    height: "5.3rem",
    borderRadius: "50%",
    border: "1px solid var(--colors-gray-400)",
    backgroundColor: "var(--colors-gray-100)",
    transform: "translateY(-50%)"
});

const selectedCheckboxCircleStyle = css({
    position: "absolute",
    top: "50%",
    right: "-2.65rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "5.3rem",
    height: "5.3rem",
    borderRadius: "50%",
    border: "1px solid var(--colors-secondary)",
    backgroundColor: "var(--colors-gray-100)",
    transform: "translateY(-50%)"
});
import {useId} from "react";
import {css} from "@panda/css";

import {Checkbox} from "@pg-design/checkbox-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";
import {MULTILEAD_RESPONSIVE_MODAL_TEST_IDS} from "@pg-mono/test-ids";

import {getPropertyBoxMultileadText} from "../../../atoms/get_property_box_multilead_text";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";

const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);

interface IProps {
    property: IRecommendedProperty;
    isSelected: boolean;
    hidePrices: boolean;
    isViewedOffer?: boolean;
    recommendationAdvantages?: {priceDifference: number; areaDifference: number};
    onToggle: () => void;
    onHover: (propertyId: number | null) => void;
}

export const ApplicationMultiLeadResponsiveModalPropertyCard = (props: IProps) => {
    const {property, isSelected, hidePrices, isViewedOffer, recommendationAdvantages, onToggle, onHover} = props;
    const titleId = useId();
    const addressId = useId();
    const comparisonText = isViewedOffer
        ? null
        : getPropertyBoxMultileadText(recommendationAdvantages?.areaDifference || 0, recommendationAdvantages?.priceDifference || 0);
    const badgeText = isViewedOffer ? "Oglądana oferta" : comparisonText;

    return (
        <li
            className={isSelected ? selectedCardStyle : cardStyle}
            onClick={onToggle}
            onMouseEnter={() => onHover(property.id)}
            onMouseLeave={() => onHover(null)}
            data-track-id={property.id}
            data-testid={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.PROPERTY_CARD}
        >
            <div className={isSelected ? selectedCardBoxStyle : cardBoxStyle}>
                <Image
                    src={getThumbnailLink(property.offer?.main_image_url, 350, 230)}
                    alt=""
                    width="99px"
                    height="119px"
                    className={imageWrapperStyle}
                    imageClassName={imageStyle}
                    placeholderIconClasName={placeholderIconStyle}
                />

                <div className={cardContentStyle}>
                    <div className={cardTextStyle}>
                        <Text as="p" id={titleId} variant="info_txt_1" color="var(--colors-secondary)">
                            {`${property.rooms} ${roomsPluralize(property.rooms)}, ${areaFormat(property.area)}`}
                        </Text>
                        <Text as="p" id={addressId} variant="info_txt_3" color="var(--colors-gray-700)" className={addressStyle}>
                            {property.offer.name}, {property.offer.address}
                        </Text>
                    </div>

                    {property.price && !hidePrices ? (
                        <Text as="p" variant="body_copy_1" strong color="var(--colors-secondary)">
                            <Highlight>{priceFormat(property.price)}</Highlight>
                        </Text>
                    ) : null}

                    {badgeText ? (
                        <div className={badgeStyle}>
                            <Text as="span" variant="info_txt_2" color="var(--colors-secondary)">
                                {badgeText}
                            </Text>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className={isSelected ? selectedCheckboxCircleStyle : checkboxCircleStyle}>
                <Checkbox
                    name={`property_${property.id}`}
                    checked={isSelected}
                    value={property.id}
                    onChange={onToggle}
                    aria-labelledby={`${titleId} ${addressId}`}
                    dataTestId={MULTILEAD_RESPONSIVE_MODAL_TEST_IDS.PROPERTY_CHECKBOX}
                />
            </div>
        </li>
    );
};

const cardStyle = css({
    position: "relative",
    cursor: "pointer"
});

const selectedCardStyle = css({
    position: "relative",
    cursor: "pointer",
    filter: "drop-shadow(0 4px 2px rgba(0, 0, 0, 0.25))"
});

const cardBoxStyle = css({
    display: "flex",
    columnGap: 1,
    overflow: "hidden",
    borderRadius: 1,
    border: "1px solid var(--colors-gray-300)",
    backgroundColor: "var(--colors-white)"
});

const selectedCardBoxStyle = css({
    display: "flex",
    columnGap: 1,
    overflow: "hidden",
    borderRadius: 1,
    border: "1px solid var(--colors-secondary)",
    backgroundColor: "var(--colors-background-100)"
});

const imageWrapperStyle = css({
    display: "block",
    flex: "0 0 9.9rem",
    alignSelf: "stretch",
    width: "9.9rem",
    height: "auto",
    minHeight: "11.9rem",
    backgroundColor: "var(--colors-gray-300)"
});

const imageStyle = css({
    position: "absolute",
    top: 0,
    left: 0,
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const placeholderIconStyle = css({
    transform: "translate(-50%, -50%) scale(8)",
    opacity: 0.5
});

const cardContentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    rowGap: 1,
    flex: "1 1 auto",
    minWidth: 0,
    py: 1,
    pr: "3.4rem"
});

const cardTextStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 0.5
});

const addressStyle = css({
    fontSize: "0.842rem",
    lineClamp: 2,
    md: {
        fontSize: "0.983rem"
    }
});

const badgeStyle = css({
    display: "inline-flex",
    alignItems: "center",
    minHeight: "2.4rem",
    px: 1,
    borderRadius: 1,
    backgroundColor: "var(--colors-beige-subtle)"
});

const checkboxCircleStyle = css({
    position: "absolute",
    top: "50%",
    right: "-2.65rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "5.3rem",
    height: "5.3rem",
    borderRadius: "50%",
    border: "1px solid var(--colors-gray-400)",
    backgroundColor: "var(--colors-gray-100)",
    transform: "translateY(-50%)"
});

const selectedCheckboxCircleStyle = css({
    position: "absolute",
    top: "50%",
    right: "-2.65rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "5.3rem",
    height: "5.3rem",
    borderRadius: "50%",
    border: "1px solid var(--colors-secondary)",
    backgroundColor: "var(--colors-gray-100)",
    transform: "translateY(-50%)"
});
